import { useState, useMemo, Fragment } from "react";
import Box from "@mui/material/Box";

import useVariant from "hooks/useVariant";
import useSelectedColor from "hooks/useSelectedColor";
import Divider from "components/Divider";
import Collapsible from "components/Collapsible";
import WheelSelector from "components/WheelSelector";
import ColorSelector from "components/ColorSelector";
import BatterySelector from "components/BatterySelector";
import VariantSelector from "components/VariantSelector";

import type { FeatureListItem } from "types/app";
import type { TypographyProps } from "@mui/material/Typography";

enum PANELS {
  VARIANTS = "variants",
  BATTERY = "battery",
  WHEELS = "wheels",
  COLOURS = "colours",
  SEATS = "seats",
}

type ConfigurationsTabProps = {
  openFeaturePreview: (options: FeatureListItem, disableAnalyticsEvent?: boolean) => void;
  closeFeaturePreview: () => void;
};

const collapsibleTitleProps: TypographyProps = {
  variant: "collapsible",
  fontSize: 16,
  fontWeight: 600,
};
const collapsibleSecondaryTextProps: TypographyProps = {
  variant: "collapsible",
  fontSize: 15,
  fontWeight: 400,
};
const activeValueProps: TypographyProps = {
  variant: "collapsible",
  fontSize: 13,
  fontWeight: 300,
  sx: { wordBreak: "break-all" },
};

type PanelMap = {
  id: PANELS;
  title: string;
  children: null | JSX.Element;
  activeValue?: string;
  secondaryValue?: string;
};

export default function ConfigurationsTab({ openFeaturePreview }: ConfigurationsTabProps) {
  const [expandedPanel, setExpandedPanel] = useState<PANELS | null>(null);

  const variant = useVariant();
  const selectedColor = useSelectedColor();

  const handlePanelExpand = (panel: PANELS) => {
    setExpandedPanel((prev) => (prev === panel ? null : panel));
  };

  const panelMap: PanelMap[] = useMemo(() => {
    return [
      {
        id: PANELS.VARIANTS,
        title: "Variants",
        children: <VariantSelector openFeaturePreview={openFeaturePreview} />,
        activeValue: variant?.variant,
      },
      {
        id: PANELS.BATTERY,
        title: "Battery",
        children: <BatterySelector />,
        activeValue: parseInt(variant?.battery || "0").toString(),
        secondaryValue: "(in kWh)",
      },
      {
        id: PANELS.WHEELS,
        title: "Wheels",
        activeValue: variant?.wheels,
        children: <WheelSelector />,
      },
      {
        id: PANELS.COLOURS,
        title: "Colours",
        children: <ColorSelector />,
        activeValue: selectedColor?.label,
      },
    ];
  }, [variant, selectedColor]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {panelMap.map(({ id, title, activeValue, secondaryValue, children }) => (
        <Fragment key={id}>
          <Box sx={{ px: 3, py: 0.5, mb: 1 }}>
            <Collapsible
              title={title}
              TitleProps={collapsibleTitleProps}
              activeValue={activeValue}
              ActiveValueProps={activeValueProps}
              secondaryValue={secondaryValue}
              SecondaryTextProps={collapsibleSecondaryTextProps}
              open={expandedPanel === id}
              marginBottom={0}
              keepChildrenMounted={false}
              handleToggle={() => handlePanelExpand(id)}
            >
              {children}
            </Collapsible>
          </Box>

          <Divider />
        </Fragment>
      ))}
    </>
  );
}
